import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Formik, Field, Form, ErrorMessage } from 'formik'
import * as Yup from 'yup';
import { AuthData } from '../../provider/AuthProvider';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../configs/firebase';

const WelcomeForm = () => {
    const navigate = useNavigate();
    const { user, updateUserProfile } = AuthData();
    const [userName, setUserName] = useState('');

    // Get user's name from their profile
    useEffect(() => {
        if (user?.profile?.personalInfo) {
            const firstName = user.profile.personalInfo.firstName || '';
            const lastName = user.profile.personalInfo.lastName || '';
            setUserName(`${firstName} ${lastName}`.trim());
        } else if (user?.userd?.displayName) {
            setUserName(user.userd.displayName);
        }
    }, [user]);

    // Updated interest options (you can fetch these from Firebase settings later)
    const checkboxOptions = [
        { id: "software_engineering", label: "Software Engineering and Development" },
        { id: "data_science", label: "Data Science and Analytics" },
        { id: "web_development", label: "Web Development" },
        { id: "mobile_development", label: "Mobile App Development" },
        { id: "ui_ux_design", label: "UI/UX Design" },
        { id: "digital_marketing", label: "Digital Marketing" },
        { id: "content_creation", label: "Content Creation and Writing" },
        { id: "business_analytics", label: "Business Analytics" },
        { id: "project_management", label: "Project Management" },
        { id: "cybersecurity", label: "Cybersecurity" },
        { id: "artificial_intelligence", label: "Artificial Intelligence & Machine Learning" },
        { id: "finance", label: "Finance and Accounting" },
        { id: "human_resources", label: "Human Resources" },
        { id: "sales", label: "Sales and Business Development" },
        { id: "consulting", label: "Consulting" },
        { id: "research", label: "Research and Development" },
        { id: "healthcare", label: "Healthcare and Medical" },
        { id: "education", label: "Education and Training" },
        { id: "non_profit", label: "Non-profit and Social Impact" },
        { id: "sustainability", label: "Sustainability and Environmental" }
    ];

    const handleSubmit = async (values, { setSubmitting, setErrors }) => {
        try {
            if (!user?.userd?.uid) {
                throw new Error('User not found. Please try logging in again.');
            }

            console.log('🎯 Updating user interests:', values.selectedInterests);

            // Update user profile with interests
            const updateData = {
                interests: values.selectedInterests,
                profileCompletion: 75, // Increase completion percentage
                onboardingCompleted: true,
                updatedAt: serverTimestamp()
            };

            // Update in Firestore
            const userRef = doc(db, 'users', user.userd.uid);
            await updateDoc(userRef, updateData);

            console.log('✅ User interests updated successfully');
            
            // Navigate to main app
            navigate("/explore");
            
        } catch (error) {
            console.error('❌ Error updating interests:', error);
            setErrors({ general: error.message });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className='flex justify-center items-center bg-gradient-to-br from-orange-400 to-orange-600 min-h-screen w-full p-4 sm:p-6 lg:p-10'>
            <div className='flex flex-col gap-6 max-w-2xl w-full'>
                {/* Header */}
                <div className='text-center'>
                    <h2 className='text-white text-3xl font-bold mb-2'>
                        Welcome, {userName}! 👋
                    </h2>
                    <p className='text-orange-100 text-lg'>
                        Let's personalize your internship experience
                    </p>
                </div>

                {/* Form Container */}
                <div className='bg-white rounded-xl shadow-xl p-8'>
                    <div className='text-center mb-6'>
                        <h3 className='text-2xl font-semibold text-gray-800 mb-2'>
                            Select Your Interests
                        </h3>
                        <p className='text-gray-600'>
                            Choose at least 3 areas you're interested in to help us recommend relevant internships
                        </p>
                    </div>

                    <Formik  
                        initialValues={{
                            selectedInterests: []
                        }}
                        validationSchema={Yup.object({
                            selectedInterests: Yup.array()
                                .min(3, "Please select at least 3 interests")
                                .required("This field is required")
                        })}
                        onSubmit={handleSubmit}
                    >
                        {({ values, errors, isSubmitting, handleChange }) => (
                            <Form className='space-y-6'>
                                {/* Interests Selection */}
                                <div className='space-y-4'>
                                    <div className='grid grid-cols-1 md:grid-cols-2 gap-3 max-h-80 overflow-y-auto p-4 border border-gray-200 rounded-lg'>
                                        {checkboxOptions.map((option) => (
                                            <label 
                                                key={option.id} 
                                                className='flex items-start gap-3 p-3 rounded-lg hover:bg-orange-50 cursor-pointer transition-colors'
                                            >
                                                <Field
                                                    type="checkbox"
                                                    name="selectedInterests" 
                                                    value={option.label}
                                                    className="w-4 h-4 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500 focus:ring-2 mt-1"
                                                />
                                                <span className="text-gray-700 text-sm leading-5">
                                                    {option.label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                    
                                    {/* Selection Counter */}
                                    <div className='text-center'>
                                        <span className={`text-sm font-medium ${
                                            values.selectedInterests.length >= 3 
                                                ? 'text-green-600' 
                                                : 'text-gray-500'
                                        }`}>
                                            {values.selectedInterests.length} selected 
                                            {values.selectedInterests.length >= 3 && ' ✓'}
                                        </span>
                                    </div>
                                    
                                    <ErrorMessage name="selectedInterests" component="div" className="text-red-500 text-sm text-center" />
                                </div>

                                {/* General Error Display */}
                                {errors.general && (
                                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                                        {errors.general}
                                    </div>
                                )}

                                {/* Submit Button */}
                                <div className='text-center'>
                                    <button 
                                        type="submit" 
                                        disabled={isSubmitting || values.selectedInterests.length < 3}
                                        className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-8 rounded-lg transition-colors min-w-[160px]"
                                    >
                                        {isSubmitting ? (
                                            <div className="flex items-center justify-center">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                                Saving...
                                            </div>
                                        ) : (
                                            'Complete Setup'
                                        )}
                                    </button>
                                </div>

                                {/* Skip Option */}
                                <div className='text-center'>
                                    <button
                                        type="button"
                                        onClick={() => navigate("/explore")}
                                        className="text-gray-500 hover:text-gray-700 text-sm underline"
                                    >
                                        Skip for now (you can update this later)
                                    </button>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>

                {/* Progress Indicator */}
                <div className='text-center text-white'>
                    <div className='flex items-center justify-center space-x-2 mb-2'>
                        <div className='w-3 h-3 bg-white rounded-full'></div>
                        <div className='w-3 h-3 bg-white rounded-full'></div>
                        <div className='w-3 h-3 bg-orange-200 rounded-full'></div>
                    </div>
                    <p className='text-orange-100 text-sm'>Step 3 of 3 - Almost done!</p>
                </div>
            </div>
        </div>
    );
};

export default WelcomeForm;